import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  all,
  call,
  put,
  retry,
  SagaReturnType,
  select,
  take,
  takeLatest
} from 'redux-saga/effects';
import { LandProfile } from 'src/models/LandProfile';
import { UnlockFlags } from 'src/models/UnlockFlags';
import { RootState } from '.';
import api from '../api/BlancoClient';
import { initializeI18n, resolveLanguage, setLanguage } from '../i18n';
import { requestGameWebToken } from '../lib/GameWebToken';
import { enableTracker, sendException } from '../lib/google-analytics';
import { PersistentData } from '../lib/PersistentData';
import { addPersistentData, restorePersistentData } from '../lib/storage';
import { Baid, validateBaid } from '../models/Baid';
import { User } from '../models/User';
import { actions as bestFriendsActions } from './bestFriends';
import { actions as catalogActions } from './catalog';
import { actions as chatActions } from './chat';
import { actions as fortuneActions } from './fortune';
import { actions as lifeSupportsActions } from './lifeSupports';
import { actions as myDesignActions } from './myDesign';
import { actions as newspaperActions } from './newspaper';
import { actions as notificationActions, setupNotifications } from './notification';
import { actions as passportActions } from './passport';
import { actions as reactionActions } from './reaction';
import { actions as resortActions } from './resort';
import { checkErrorType, Progress } from './utils/helper';

export type UserErrorType =
  | 'player-profile-not-found'
  | 'player-not-in-island'
  | 'unstable-network'
  | 'unknown';

export interface UserState {
  initialized: boolean;
  users: User[];
  selectedUserId?: string;
  baid?: string;
  expireAt?: number;
  errorType?: UserErrorType;
  isLoading: boolean;
  page: 1 | 2; // ホーム画面のアプリ一覧のページ番号
  selectStatus?: Progress;
  isLanguageUpdating?: boolean;
  splashFinished?: boolean;
  reportedWarningFinished?: boolean;
  unlockFlags?: UnlockFlags; // リアクション及びツクッターに関する解禁情報のフラグのオブジェクト
  language?: string;
  country?: string; // Coralから渡されたNAの国情報
  marked?: PersistentData['marked']; // 既に初回チュートリアルを見たかどうかのフラグ
  causeServerError?: boolean; // サーバーエラーが出現したかどうかのフラグ
  serverErrorCode?: string; // サーバーエラーのエラーコード reference: https://spdlybra.nintendo.co.jp/jira/browse/BLANCO-93
}

const initialState: UserState = {
  initialized: false,
  users: [],
  isLoading: true,
  page: 1
};

interface SelectUserPayload {
  selectedUserId: string;
  users?: User[];
  language: string;
}

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setupLanguage: (state, { payload }: PayloadAction<{ language: string; country: string }>) => {
      // NOTE: payloadのlanguageはnavigator.languageなのでここではstoreに保存しない。
      // storeにはBlancoが用意している言語に解決した後に保存する。
      state.country = payload.country;
    },

    storageRestored: (state, { payload }: PayloadAction<PersistentData>) => {
      state.initialized = true;
      state.selectedUserId = payload.userId;
      state.marked = payload.marked;
      state.language = payload.language;
    },

    getUserList: state => {
      state.isLoading = true;
      state.errorType = undefined;
    },

    getUserListSucceeded: (state, { payload }: PayloadAction<User[]>) => {
      state.users = payload;
      state.isLoading = false;
      state.errorType = undefined;
    },

    getUserListFailed: (state, { payload }: PayloadAction<UserErrorType>) => {
      state.isLoading = false;
      state.errorType = payload;
    },

    updateUserList: state => {
      state.isLoading = true;
      state.errorType = undefined;
    },

    selectUser: (state, { payload }: PayloadAction<SelectUserPayload>) => {
      state.isLoading = false;
      state.selectStatus = 'loading';
      state.errorType = undefined;
      if (payload.users) {
        state.users = payload.users;
      }
    },

    selectUserSucceeded: (
      state,
      { payload }: PayloadAction<{ selectedUserId: string; credentials: Baid; marked: boolean }>
    ) => {
      state.selectStatus = 'succeeded';
      state.selectedUserId = payload.selectedUserId;
      state.baid = payload.credentials.token;
      state.expireAt = payload.credentials.expireAt;
      state.splashFinished = false;
      state.page = 1;
    },

    selectUserFailed: (state, { payload }: PayloadAction<UserErrorType>) => {
      state.selectStatus = 'failed';
      state.errorType = payload;
    },

    setPage: (state, { payload }: PayloadAction<{ page: 1 | 2 }>) => {
      state.page = payload.page;
    },

    reselectUser: (state, _: PayloadAction<SelectUserPayload>) => {
      state.selectStatus = 'loading';
    },

    refreshTokenSucceeded: (state, { payload }: PayloadAction<Baid>) => {
      state.baid = payload.token;
      state.expireAt = payload.expireAt;
    },

    refreshTokenFailed: state => {
      state.baid = undefined;
      state.expireAt = undefined;
    },

    updateLanguage: (state, _: PayloadAction<string>) => {
      state.isLanguageUpdating = true;
      state.errorType = undefined;
    },

    updateLanguageSucceeded: (state, { payload }: PayloadAction<string>) => {
      state.language = payload;
      state.isLanguageUpdating = false;
      state.splashFinished = false;
      state.page = 1;
    },

    updateLanguageFailed: state => {
      state.isLanguageUpdating = false;
      state.errorType = 'unknown';
    },

    clearSelectStatus: state => {
      state.selectStatus = undefined;
    },

    splashFinished: state => {
      state.splashFinished = true;
      state.marked = true;
    },

    reportedWarningFinished: state => {
      state.reportedWarningFinished = true;
    },

    getUnlockFlagsSucceeded: (state, { payload }: PayloadAction<UnlockFlags>) => {
      state.unlockFlags = payload;
    },

    // this payload is error code e.g. '3997','3999'
    causeServerError: (state, { payload }: PayloadAction<string | undefined>) => {
      state.causeServerError = true;
      state.serverErrorCode = payload || '3999';
    }
  }
});

export default slice.reducer;

const {
  setupLanguage,
  getUserList,
  updateUserList,
  selectUser,
  setPage,
  reselectUser,
  updateLanguage,
  splashFinished,
  reportedWarningFinished,
  causeServerError,
  ...internalActions
} = slice.actions;

export const actions = {
  setupLanguage,
  getUserList,
  updateUserList,
  selectUser,
  setPage,
  reselectUser,
  updateLanguage,
  splashFinished,
  reportedWarningFinished,
  causeServerError
};

const {
  storageRestored,
  getUserListSucceeded,
  getUserListFailed,
  selectUserSucceeded,
  selectUserFailed,
  refreshTokenSucceeded,
  refreshTokenFailed,
  clearSelectStatus,
  updateLanguageSucceeded,
  updateLanguageFailed,
  getUnlockFlagsSucceeded
} = internalActions;

// Saga

export function* userSaga() {
  const setupLanguageAction: ReturnType<typeof setupLanguage> = yield take(setupLanguage.type);
  let userId: PersistentData['userId'];
  let marked: PersistentData['marked'];
  let language: PersistentData['language'];
  let userNotificationStates: PersistentData['userNotificationStates'];
  let userReactionStates: PersistentData['userReactionStates'];
  let userCatalogTodayStates: PersistentData['userCatalogTodayStates'];
  let userCatalogSearchStates: PersistentData['userCatalogSearchStates'];
  let userCatalogNoticeStates: PersistentData['userCatalogNoticeStates'];
  let userLifeSupportsTipHistoryStates: PersistentData['userLifeSupportsTipHistoryStates'];
  let userNewspaperStates: PersistentData['userNewspaperStates'];

  try {
    const data: PersistentData = yield retry(10, 1000, restorePersistentData);
    userId = data.userId;
    marked = data.marked;
    language = data.language;
    userNotificationStates = data.userNotificationStates;
    userReactionStates = data.userReactionStates;
    userCatalogTodayStates = data.userCatalogTodayStates;
    userCatalogSearchStates = data.userCatalogSearchStates;
    userCatalogNoticeStates = data.userCatalogNoticeStates;
    userLifeSupportsTipHistoryStates = data.userLifeSupportsTipHistoryStates;
    userNewspaperStates = data.userNewspaperStates;
    if (language) {
      yield call(initializeI18n, language);
    } else {
      language = resolveLanguage(
        setupLanguageAction.payload.language,
        setupLanguageAction.payload.country
      );
      yield call(initializeI18n, language);
    }
  } catch {}

  // storage restored
  const persistedData: PersistentData = {
    userId,
    marked,
    language,
    userNotificationStates,
    userReactionStates,
    userCatalogTodayStates,
    userCatalogSearchStates,
    userCatalogNoticeStates,
    userLifeSupportsTipHistoryStates,
    userNewspaperStates
  };
  yield put(notificationActions.persistentDataRestored(persistedData));
  yield put(reactionActions.persistentDataRestored(persistedData));
  yield put(catalogActions.persistentDataRestored(persistedData));
  yield put(lifeSupportsActions.persistentDataRestored(persistedData));
  yield put(newspaperActions.persistentDataRestored(persistedData));
  yield put(storageRestored(persistedData));

  yield takeLatest(getUserList.type, getUserListSaga);
  yield takeLatest(updateUserList.type, updateUserListSaga);
  yield takeLatest(selectUser.type, selectUserSaga);
  yield takeLatest(reselectUser.type, selectUserSaga);
  yield takeLatest(splashFinished.type, splashFinishedSaga);
  yield takeLatest(updateLanguage.type, updateLanguageSaga);
}

function* getUserListSaga() {
  const userState: UserState = yield select((state: RootState) => state.user);
  const { selectedUserId, language } = userState;
  if (!language) {
    // 復帰不可能のエラー
    throw new Error();
  }

  try {
    const { users, dnt } = yield call(api.getUserList);
    if (!dnt) {
      enableTracker();
    }
    if (users.length === 1) {
      const { id } = users[0];
      yield put(selectUser({ selectedUserId: id, users, language }));
    } else {
      const selected = users.find((u: User) => u.id === selectedUserId);
      if (selected) {
        yield put(selectUser({ selectedUserId: selected.id, users, language }));
      } else {
        yield put(getUserListSucceeded(users));
      }
    }
  } catch (error) {
    console.error(error.message);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(actions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getUserListFailed('unstable-network'));
    } else {
      yield put(getUserListFailed('unknown'));
    }
  }
}

function* updateUserListSaga() {
  try {
    const { users } = yield call(api.getUserList);
    yield put(getUserListSucceeded(users));
  } catch (error) {
    console.error(error.message);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(actions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(getUserListFailed('unstable-network'));
    } else {
      yield put(getUserListFailed('unknown'));
    }
  }
}

function* selectUserSaga(action: { type: typeof selectUser.type; payload: SelectUserPayload }) {
  const { selectedUserId } = action.payload;
  const data: SagaReturnType<typeof restorePersistentData> = yield call(restorePersistentData);

  const marked = data.marked || false;
  try {
    const { token, expireAt } = yield call(api.postAuthToken, selectedUserId);
    const credentials = { token, expireAt };

    yield call(getInitialData, selectedUserId, token);
    yield call(setupNotifications, selectedUserId);

    yield put(bestFriendsActions.reset());
    yield put(chatActions.reset());
    yield put(myDesignActions.reset());
    yield put(passportActions.reset());
    yield put(reactionActions.resetReactionItems());
    yield put(catalogActions.reset());
    yield put(fortuneActions.reset());
    yield put(resortActions.reset());
    yield put(newspaperActions.reset());
    yield put(selectUserSucceeded({ selectedUserId, credentials, marked }));

    yield put(clearSelectStatus());
    yield call(addPersistentData, { userId: selectedUserId });
  } catch (error) {
    console.error(error.message);
    const errorType = checkErrorType(error);
    if (errorType === 'server') {
      yield put(actions.causeServerError(error.response.data?.code));
    } else if (errorType === 'unstable-network') {
      yield put(selectUserFailed('unstable-network'));
    } else {
      if (error.response.data?.code) {
        const { code } = error.response.data;
        const userErrorType = getUserErrorType(code);
        yield put(selectUserFailed(userErrorType));
      } else {
        yield put(selectUserFailed('unknown'));
      }
    }
  }
}

function* splashFinishedSaga() {
  try {
    yield addPersistentData({ marked: true });
  } catch {}
}

function* updateLanguageSaga(action: { type: typeof updateLanguage.type; payload: string }) {
  try {
    const language = action.payload;
    yield call(setLanguage, language);
    yield call(addPersistentData, { language });

    yield put(passportActions.reset());
    yield put(reactionActions.resetReactionItems());
    yield put(catalogActions.reset());
    yield put(fortuneActions.reset());
    yield put(resortActions.reset());
    yield put(newspaperActions.reset());

    yield put(updateLanguageSucceeded(language));
  } catch (error) {
    console.error(error.message);
    sendException('Language update failed');
    yield put(updateLanguageFailed());
  }
}

// FIXME: 型定義
export function* getFreshToken() {
  const { baid, expireAt, selectedUserId }: UserState = yield select(
    (state: RootState) => state.user
  );
  if (validateBaid(baid, expireAt)) {
    return baid;
  } else {
    if (!selectedUserId) {
      throw new Error();
    }
    try {
      const gwt: SagaReturnType<typeof requestGameWebToken> = yield call(requestGameWebToken);
      const tokenResponse: SagaReturnType<typeof api.postAuthToken> = yield call(
        api.postAuthToken,
        selectedUserId,
        gwt
      );
      yield put(refreshTokenSucceeded(tokenResponse));
      return tokenResponse.token;
    } catch (error) {
      const errorType = checkErrorType(error);
      if (errorType === 'server') {
        yield put(actions.causeServerError(error.response.data?.code));
      } else {
        yield put(refreshTokenFailed());
      }
      sendException('Refreshing token failed', true);
    }
  }
}

/* 初回起動時に用意する必要があるプレイヤー情報を取得します */
function* getInitialData(userId: string, baid: string) {
  const userState: UserState = yield select((state: RootState) => state.user);
  const { users, language } = userState;

  const selectedUser = users.find(u => u.id === userId);
  if (!selectedUser || !language) {
    // 復帰不可能のエラー
    throw new Error('Data initialization failed');
  }

  try {
    const [landProfile, unlockFlags]: [LandProfile, UnlockFlags] = yield all([
      call(api.getLandProfile, selectedUser.land.id, language, baid),
      call(api.getUnlockFlags, baid)
    ]);
    yield put(getUnlockFlagsSucceeded(unlockFlags));
    yield put(notificationActions.getIslandProfileSucceeded(landProfile));
  } catch (error) {
    //　エラー処理は呼び出しもとに委譲
    throw error;
  }
}

// helper function
function getUserErrorType(code?: string) {
  switch (code) {
    case '1102':
      return 'player-profile-not-found';
    case '1401':
      return 'player-not-in-island';
    default:
      return 'unknown';
  }
}
